import arrowIcon from '@assets/icons/arrow.svg';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { CircularProgress } from '@components/shared/circular-progressbar.component';
import { ProgressBar } from '@components/shared/progressbar.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { formatPercentage } from '@utils/number.util';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { handleCollapsedSidebarItemClick } from '../../survey/components/survey-sidebar-click.utils';

type AssessmentSidebarSection = {
  answered: number;
  href: string;
  id: string;
  title: string;
  total: number;
};

type AssessmentSidebarChapter = {
  checklist?: ReactNode;
  collapsedLabel?: string;
  href: string;
  id: string;
  label?: string;
  progress: number;
  sections: AssessmentSidebarSection[];
  statusBadge?: {
    label: string;
    tooltip?: ReactNode;
    variant?: BadgeProps['variant'];
  };
  subtitle?: string;
  title: string;
};

type AssessmentSidebarProps = {
  chapters?: AssessmentSidebarChapter[];
  currentChapterId?: string;
  currentSectionId?: string;
  isCollapsed?: boolean;
  isSubmitting?: boolean;
  onNavigate: (href: string) => void;
  onSubmit?: () => void;
  setIsCollapsed?: (isCollapsed: boolean) => void;
  showChapterLabels?: boolean;
  showProgress?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;
  submitTooltip?: string;
  totalProgress?: number;
};

export const AssessmentSidebar: React.FC<AssessmentSidebarProps> = (props) => {
  const {
    chapters,
    currentChapterId,
    currentSectionId,
    isCollapsed,
    isSubmitting,
    onNavigate,
    onSubmit,
    setIsCollapsed,
    showChapterLabels = true,
    showProgress = true,
    submitDisabled,
    submitLabel = 'Submit',
    submitTooltip,
    totalProgress,
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const chapterRef = useRef<HTMLDivElement>(null);
  const [chapterScrollTrigger, setChapterScrollTrigger] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Scroll when the active chapter changes or the sidebar re-expands
  useEffect(() => {
    if (chapterRef.current && scrollContainerRef.current) {
      const chapter = chapterRef.current;
      const chapterTop = chapter.getBoundingClientRect().top;
      const container = scrollContainerRef.current;
      const containerTop = container.getBoundingClientRect().top;
      const scrollOffset = chapterTop - containerTop + container.scrollTop;

      container.scrollTo({ behavior: 'instant', top: scrollOffset });
    }
  }, [chapterScrollTrigger, currentChapterId]);

  useEffect(() => {
    if (!isCollapsed) {
      const timeoutId = setTimeout(() => {
        setChapterScrollTrigger(Math.random());
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [isCollapsed]);

  const submitButton = (
    <Button
      className="w-full"
      disabled={submitDisabled}
      loading={isSubmitting}
      onClick={onSubmit}
    >
      {submitLabel}
    </Button>
  );
  const submitIconButton = (
    <IconButton
      className="bg-blue-600 hover:bg-blue-700"
      disabled={submitDisabled}
      icon={
        <img
          className={submitDisabled ? 'opacity-40' : ''}
          src={arrowIcon}
        />
      }
      loading={isSubmitting}
      onClick={onSubmit}
      size="md"
    />
  );

  const handleExpandCollapsedSidebar = (): void => {
    setIsCollapsed?.(false);
  };
  const resolvedTotalProgress =
    totalProgress ??
    (chapters?.length
      ? chapters.reduce((accumulator, chapter) => accumulator + chapter.progress, 0) / chapters.length
      : 0);

  return (
    <div
      className={cn(
        'flex h-full shrink-0 flex-col overflow-hidden transition-[width] duration-300 ease-in-out',
        isCollapsed
          ? 'w-[52px] cursor-w-resize'
          : 'w-[136px] @min-[36rem]/survey-shell:w-[160px] @min-[42rem]/survey-shell:w-[200px] @min-[48rem]/survey-shell:w-[240px] @min-[64rem]/survey-shell:w-[272px] @min-[80rem]/survey-shell:w-[320px] @min-[96rem]/survey-shell:w-[360px]',
      )}
      style={{
        backgroundColor: '#09005C',
        backgroundImage:
          'radial-gradient(65.88% 80.53% at 65.88% 19.47%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 100%)',
      }}
    >
      {isCollapsed ? (
        <>
          <div
            className="flex grow cursor-w-resize flex-col items-center gap-2 px-1.5 py-4"
            onClick={() => setIsCollapsed?.(!isCollapsed)}
          >
            {chapters && chapters.length > 0 ? (
              <>
                {chapters.slice(0, 6).map((chapter, index) => {
                  const collapsedChapterIsCurrent = currentChapterId === chapter.id;

                  return (
                    <div
                      className={cn(
                        'flex w-full items-center justify-center rounded-md border px-1 py-2 text-[10px] font-semibold leading-none transition-colors',
                        collapsedChapterIsCurrent
                          ? 'border-[#8D83FF] bg-white/30 text-white'
                          : 'border-white/20 bg-white/10 text-white/70',
                      )}
                      key={chapter.id}
                      onClick={(event) =>
                        handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                      }
                    >
                      {chapter.collapsedLabel ?? index + 1}
                    </div>
                  );
                })}
                {chapters.length > 6 && (
                  <div
                    className="pt-1 text-[10px] font-medium text-white/70"
                    onClick={(event) =>
                      handleCollapsedSidebarItemClick({ event, onExpand: handleExpandCollapsedSidebar })
                    }
                  >
                    +{chapters.length - 6}
                  </div>
                )}
              </>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-center gap-y-2 bg-[#1D1946] px-2 py-4">
            {showProgress && chapters && chapters.length > 0 && (
              <>
                <div className="h-1 w-8 overflow-hidden rounded-full bg-[#F2F2F2]">
                  <div
                    className="h-full rounded-full bg-[#00A369] transition-all duration-300"
                    style={{
                      width: `${Math.round(resolvedTotalProgress * 100)}%`,
                    }}
                  />
                </div>
                <div className="text-xs font-medium text-[#00A369]">{formatPercentage(resolvedTotalProgress)}</div>
              </>
            )}
            {onSubmit && (
              <div className="pt-2">
                {submitTooltip ? (
                  <TooltipComponent
                    followCursor={true}
                    side="top"
                    trigger={<div>{submitIconButton}</div>}
                  >
                    {submitTooltip}
                  </TooltipComponent>
                ) : (
                  submitIconButton
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="h-7 shrink-0" />
          <div
            className="min-h-0 w-full flex-1 basis-0 overflow-y-auto px-6 pb-7"
            ref={scrollContainerRef}
          >
            <div className="flex flex-col gap-y-3">
              {chapters?.map((chapter, index) => {
                const isCurrentChapter = currentChapterId === chapter.id;
                const shouldUseReadinessCard = Boolean(chapter.checklist || chapter.statusBadge || chapter.subtitle);

                if (shouldUseReadinessCard) {
                  const statusBadge = chapter.statusBadge ? (
                    <Badge
                      size="sm"
                      variant={chapter.statusBadge.variant}
                    >
                      {chapter.statusBadge.label}
                    </Badge>
                  ) : null;

                  return (
                    <div
                      key={chapter.id}
                      ref={isCurrentChapter ? chapterRef : null}
                    >
                      <div
                        className={cn(
                          'group/readiness flex w-full cursor-pointer flex-col rounded-[12px] p-[16px] hover:bg-white',
                          isCurrentChapter ? 'bg-white' : 'bg-[#ffffff29]',
                        )}
                        data-current={isCurrentChapter ? 'true' : 'false'}
                        onClick={() => onNavigate(chapter.href)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <div
                              className={cn(
                                'text-[15px] font-semibold leading-5 group-hover/readiness:text-[#1f1c1a]',
                                isCurrentChapter ? 'text-[#1f1c1a]' : 'text-white',
                              )}
                            >
                              {chapter.title}
                            </div>
                            {chapter.subtitle && (
                              <div
                                className={cn(
                                  'text-xs font-semibold group-hover/readiness:text-[#3d2eff]',
                                  isCurrentChapter ? 'text-[#3d2eff]' : 'text-[#8d83ff]',
                                )}
                              >
                                {chapter.subtitle}
                              </div>
                            )}
                          </div>
                          {chapter.statusBadge?.tooltip ? (
                            <TooltipComponent
                              side="top"
                              trigger={<div className="shrink-0">{statusBadge}</div>}
                            >
                              {chapter.statusBadge.tooltip}
                            </TooltipComponent>
                          ) : (
                            <div className="shrink-0">{statusBadge}</div>
                          )}
                        </div>
                        {chapter.checklist && <div className="mt-4">{chapter.checklist}</div>}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={chapter.id}
                    ref={isCurrentChapter ? chapterRef : null}
                  >
                    <div
                      className={cn(
                        'group flex w-full cursor-pointer flex-col rounded-[12px] p-[16px] hover:bg-white',
                        isCurrentChapter ? 'bg-white' : 'bg-[#ffffff29]',
                      )}
                      onClick={() => onNavigate(chapter.href)}
                    >
                      <div className="flex items-center justify-between">
                        {showChapterLabels && (
                          <div
                            className={cn(
                              'text-[14px] font-semibold group-hover:text-[#3d2eff]',
                              isCurrentChapter ? 'text-[#3d2eff]' : 'text-[#8d83ff]',
                            )}
                          >
                            {chapter.label ?? `Part ${index + 1}`}
                          </div>
                        )}
                        {showProgress && (
                          <CircularProgress
                            percentage={chapter.progress}
                            ringThickness={2}
                            size={15}
                          />
                        )}
                      </div>
                      <div
                        className={cn(
                          'mr-[32px] text-[16px] font-normal group-hover:text-[#1f1c1a]',
                          showChapterLabels && 'mt-[6px]',
                          isCurrentChapter ? 'text-[#1f1c1a]' : 'text-[#ffffff]',
                        )}
                      >
                        {chapter.title}
                      </div>
                      {isCurrentChapter && chapter.sections.length > 0 && (
                        <div
                          className="cursor-default pt-[6px]"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="mb-[5px] text-[12px] text-[#1F1C1A99]">Sections</div>
                          <div className="flex flex-col gap-[4px]">
                            {chapter.sections.map((section) => {
                              const isCurrentSection = currentSectionId === section.id;

                              return (
                                <div
                                  className={cn(
                                    '-mx-4 flex min-w-0 cursor-pointer px-4 py-[4px] text-[14px] hover:bg-gray-200',
                                    isCurrentSection
                                      ? 'bg-gray-100 font-medium text-[#3d2eff]'
                                      : 'font-normal text-gray-500',
                                  )}
                                  key={section.id}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onNavigate(section.href);
                                  }}
                                >
                                  <div className="min-w-0 flex-1 line-clamp-2 leading-[22px]">{section.title}</div>
                                  <div className="ml-[8px]">
                                    {section.answered}/{section.total}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={cn('shrink-0 bg-[#1D1946] px-5 pt-4 pb-6', onSubmit ? 'h-[130px]' : 'h-[50px]')}>
            <div className="mb-2 flex items-center justify-between">
              {showProgress && chapters && chapters.length > 0 && <ProgressBar progress={resolvedTotalProgress} />}
            </div>
            {onSubmit && (
              <div className="pt-2">
                {submitTooltip ? (
                  <TooltipComponent
                    followCursor={true}
                    side="top"
                    trigger={<div>{submitButton}</div>}
                  >
                    {submitTooltip}
                  </TooltipComponent>
                ) : (
                  submitButton
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
